import { CUSTOM_ELEMENTS_SCHEMA, LOCALE_ID, NgModule, inject, provideAppInitializer } from '@angular/core';
import { Router } from '@angular/router';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { environment } from '../environments/environment';
import { initializeApp } from './app-initializer';
import { BASE_PATH as BASE_PATH_CONTRACTS } from 'data-management-contract';
import { BASE_PATH as BASE_PATH_DICTIONARIES } from 'data-management-dictionary';
import { BASE_PATH as BASE_PATH_FEATURE_TOGGLE } from 'feature-toggle-generated-api';
import { BASE_PATH as BASE_PATH_TRANSLATION } from 'translations-data-access';
import { BASE_PATH as BASE_PATH_SANTANDER_DICTIONARY } from 'santander-contract-management-generated-api-dictionary';
import { BASE_PATH as BASE_PATH_SANTANDER_JOURNEY } from 'santander-contract-management-generated-api-journey';
import { BASE_PATH as BASE_PATH_SANTANDER_POLICY } from 'santander-contract-management-generated-api-policy';
import { BASE_PATH as BASE_PATH_IDENTITY_MANAGEMENT } from 'identity-management';
import { BASE_PATH as BASE_PATH_REPORTS } from 'sales-reports-generated-api';
import { NxIsoDateModule } from '@allianz/ng-aquila/iso-date-adapter';
import { LoaderComponent, ModalLayoutComponent } from 'shared-ui';
import { SessionHandlerService } from './services/session-handler/session-handler.service';
import { httpInterceptorProviders } from './interceptor';
import { ERROR_DEFAULT_OPTIONS } from '@allianz/ng-aquila/base';
import {
  FEATURE_TOGGLE_URL_TOKEN,
  FeatureToggleService,
  LobLinkEntryService,
  ProductsService,
  PAYMENT_URL,
  UserService,
} from 'shared-data-access';

// NOTE required for NxIsoDateAdapter and SetBirthDateValidationsService
import 'dayjs/locale/en';
import 'dayjs/locale/nl';
import 'dayjs/locale/de';
import 'dayjs/locale/fr';
import 'dayjs/locale/sv';
import 'dayjs/locale/cs';
import 'dayjs/locale/it';
import 'dayjs/locale/nl-be';
import 'dayjs/locale/de-ch';
import 'dayjs/locale/fr-ch';
import 'dayjs/locale/it-ch';
import 'dayjs/locale/en-gb';
import 'dayjs/locale/de-at';
import 'dayjs/locale/es';
import 'dayjs/locale/sk';
import { NxDatepickerIntl } from '@allianz/ng-aquila/datefield';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import { CustomTranslationLoaderService, TRANSLATION_URL_TOKEN } from 'translations-data-access';
import { TranslateUtilModule } from 'translations-utils';
import {
  ErrorsDictionaryModule,
  SessionStorageService,
  DatePickerLanguages,
  COUNTRY_CODE,
  APP_CONFIG,
  USER_EMAIL,
  USER_NAME,
  LanguageCode,
  PROVIDER_ID,
} from 'shared-utils';
import { DataManagementLinksModule, LinksService } from 'external-links-data-access';

import nl from '@angular/common/locales/nl';
import sv from '@angular/common/locales/sv';
import de from '@angular/common/locales/de';
import cs from '@angular/common/locales/cs';
import sk from '@angular/common/locales/sk';
import it from '@angular/common/locales/it';
import en_SE from '@angular/common/locales/en-SE';
import fr_BE from '@angular/common/locales/fr-BE';
import nl_BE from '@angular/common/locales/nl-BE';
import de_BE from '@angular/common/locales/de-BE';
import de_CH from '@angular/common/locales/de-CH';
import de_AT from '@angular/common/locales/de-AT';
import fr_CH from '@angular/common/locales/fr-CH';
import it_CH from '@angular/common/locales/it-CH';
import en_GB from '@angular/common/locales/en-GB';
import es from '@angular/common/locales/es';

import { registerLocaleData } from '@angular/common';
import { AuthService } from 'auth';
import { NO_MARGIN } from '@allianz/taly-core';
import { provideLogger } from 'shell-feature';

(function registerLocalesData(): void {
  const localesRegisterList: [unknown, LanguageCode][] = [
    [nl, LanguageCode.NL],
    [de, LanguageCode.DE],
    [cs, LanguageCode.CS],
    [it, LanguageCode.IT],
    [fr_BE, LanguageCode.FR_BE],
    [nl_BE, LanguageCode.NL_BE],
    [de_BE, LanguageCode.DE_BE],
    [de_CH, LanguageCode.DE_CH],
    [de_AT, LanguageCode.DE_AT],
    [fr_CH, LanguageCode.FR_CH],
    [it_CH, LanguageCode.IT_CH],
    [en_SE, LanguageCode.EN_SE],
    [sk, LanguageCode.SK],
    [sv, LanguageCode.SV],
    [en_GB, LanguageCode.EN_GB],
    [es, LanguageCode.ES],
  ];

  localesRegisterList.forEach(([locale, code]: [string, LanguageCode]) => registerLocaleData(locale, code));
})();

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    AppRoutingModule,
    NxIsoDateModule,
    ErrorsDictionaryModule,
    LoaderComponent,
    ModalLayoutComponent,
    DataManagementLinksModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useClass: CustomTranslationLoaderService,
        deps: [HttpClient],
      },
    }),
    TranslateUtilModule.forRoot(),
  ],
  providers: [
    {
      provide: 'rsaNlAdapterUrl',
      useValue: environment.adapterUrls.rsaNl,
    },
    {
      provide: 'partyServiceUrl',
      useValue: environment.partyUrl,
    },
    {
      provide: 'policyServiceUrl',
      useValue: environment.policyUrl,
    },
    {
      provide: 'notificationUrl',
      useValue: environment.notificationUrl,
    },
    {
      provide: 'identityManagementUrl',
      useValue: environment.identityManagementUrl,
    },
    {
      provide: BASE_PATH_IDENTITY_MANAGEMENT,
      useValue: environment.identityManagementUrl,
    },
    {
      provide: 'partnersPowerbiUrl',
      useValue: environment.partnersPowerbiUrl,
    },
    {
      provide: BASE_PATH_REPORTS,
      useValue: environment.reportAdapterUrl,
    },
    {
      provide: FEATURE_TOGGLE_URL_TOKEN,
      useValue: environment.featureToggleUrl,
    },
    {
      provide: TRANSLATION_URL_TOKEN,
      useValue: environment.translationUrl,
    },
    provideAppInitializer(() =>
      initializeApp(
        inject(AuthService),
        inject(UserService),
        inject(SessionHandlerService),
        inject(Router),
        inject(SessionStorageService),
        inject(LobLinkEntryService),
        inject(ProductsService),
        inject(FeatureToggleService),
        inject(TranslateService),
        inject(LinksService)
      )()
    ),
    {
      provide: LOCALE_ID,
      useFactory: (userService: UserService): string => userService.getLocale(),
      deps: [UserService],
    },
    {
      provide: PROVIDER_ID,
      useFactory: (userService: UserService): string => userService.getProviderId(),
      deps: [UserService],
    },
    {
      provide: COUNTRY_CODE,
      useFactory: (userService: UserService): string => userService.getCountry(),
      deps: [UserService],
    },
    {
      provide: USER_EMAIL,
      useFactory:
        (userService: UserService): (() => string) =>
        () =>
          userService.getUserEmail(),
      deps: [UserService],
    },
    {
      provide: USER_NAME,
      useFactory:
        (userService: UserService): (() => string) =>
        () =>
          userService.getUserName(),
      deps: [UserService],
    },
    {
      provide: APP_CONFIG,
      useValue: environment,
    },
    ...httpInterceptorProviders,
    { provide: BASE_PATH_CONTRACTS, useValue: environment.partyUrl },
    { provide: BASE_PATH_DICTIONARIES, useValue: environment.partyUrl },
    { provide: BASE_PATH_FEATURE_TOGGLE, useValue: environment.featureToggleUrl },
    { provide: BASE_PATH_TRANSLATION, useValue: environment.translationUrl },
    { provide: BASE_PATH_SANTANDER_DICTIONARY, useValue: environment.santanderUrl },
    { provide: BASE_PATH_SANTANDER_JOURNEY, useValue: environment.santanderUrl },
    { provide: BASE_PATH_SANTANDER_POLICY, useValue: environment.santanderUrl },
    { provide: ERROR_DEFAULT_OPTIONS, useValue: { appearance: 'text' } },
    { provide: NxDatepickerIntl, useClass: DatePickerLanguages },
    provideHttpClient(withInterceptorsFromDi()),
    { provide: PAYMENT_URL, useValue: environment.paymentUrl },
    {
      provide: NO_MARGIN,
      useValue: false,
    },
    provideLogger(environment),
  ],
})
export class AppModule {}
